import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CountryCode } from 'libphonenumber-js/max';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchableSelect';
import { buildPhoneCountryOptions, type PhoneCountryOption } from './countryOptions';

interface CountrySelectProps {
  country: CountryCode;
  onChange: (nextCountry: CountryCode) => void;
  inputId: string;
  isDisabled?: boolean;
  hasError?: boolean;
}

function findCountryOption(
  countryOptions: PhoneCountryOption[],
  country: CountryCode
): PhoneCountryOption | undefined {
  return countryOptions.find(function matches(option) {
    return option.value === country;
  });
}

export function CountrySelect({
  country,
  onChange,
  inputId,
  isDisabled = false,
  hasError = false,
}: CountrySelectProps) {
  const { t, i18n } = useTranslation('common');
  const activeLanguage =
    i18n.resolvedLanguage === undefined ? i18n.language : i18n.resolvedLanguage;

  const countryOptions = useMemo(
    function computeCountryOptions() {
      return buildPhoneCountryOptions(activeLanguage);
    },
    [activeLanguage]
  );

  return (
    <SearchableSelect
      inputId={inputId}
      ariaLabel={t('phoneInput.countryAriaLabel')}
      options={countryOptions}
      value={country}
      isDisabled={isDisabled}
      hasError={hasError}
      menuClassName="min-w-[18rem]"
      onChange={function handleCountryChange(nextValue) {
        if (nextValue.length > 0) {
          onChange(nextValue as CountryCode);
        }
      }}
      formatOptionLabel={function renderCountryOption(option: SearchableSelectOption, meta) {
        const matchedOption = findCountryOption(countryOptions, option.value as CountryCode);
        if (matchedOption === undefined) {
          return option.label;
        }
        if (meta.context === 'value') {
          return matchedOption.flagEmoji + ' ' + matchedOption.dialCodeText;
        }
        return (
          matchedOption.flagEmoji +
          ' ' +
          matchedOption.countryName +
          ' ' +
          matchedOption.dialCodeText
        );
      }}
    />
  );
}
